import * as React from "react";

import { RobotoDomainException } from "@/shared/http/exceptions";
import { LoggerService } from "@/shared/services";

interface BulkLayoutMutation {
  run: <TItem>(
    items: TItem[],
    mutate: (item: TItem) => Promise<unknown>,
    onDone: () => void,
  ) => void;
  isPending: boolean;
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
}

/**
 * Shared plumbing for the layout-management modals, which each apply one mutation across a set of
 * layouts (move, rename-folder, delete-folder, delete). Owns the pending flag and error mapping so
 * the modals only supply the per-item operation, a fallback message, and what to do on success.
 *
 * Failures are per-item, not all-or-nothing: every mutation is attempted, and items that succeed
 * stay applied (each mutation syncs the query cache in its own onSuccess, so the list reflects
 * them immediately). `onDone` fires only when every item succeeds; on any failure it is skipped
 * and `errorMessage` reports how many items succeeded/failed, so the calling modal stays open
 * over the partially-applied result and the user can retry or cancel.
 */
export function useBulkLayoutMutation(
  errorFallback: string,
): BulkLayoutMutation {
  const [isPending, setIsPending] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const run = React.useCallback(
    <TItem>(
      items: TItem[],
      mutate: (item: TItem) => Promise<unknown>,
      onDone: () => void,
    ): void => {
      setErrorMessage(null);
      setIsPending(true);
      // allSettled never rejects, so the chain can't produce an unhandled rejection.
      void Promise.allSettled(items.map(mutate))
        .then((results) => {
          const rejections = results.filter(
            (result): result is PromiseRejectedResult =>
              result.status === "rejected",
          );
          if (rejections.length === 0) {
            onDone();
            return;
          }

          const firstError: unknown = rejections[0].reason;
          LoggerService.error(errorFallback, firstError);
          const mappedMessage =
            firstError instanceof RobotoDomainException &&
            firstError.isClientError()
              ? firstError.message
              : errorFallback;
          const succeeded = results.length - rejections.length;
          setErrorMessage(
            `${succeeded} of ${results.length} layouts succeeded. ` +
              `${rejections.length} failed: ${mappedMessage}`,
          );
        })
        .finally(() => setIsPending(false));
    },
    [errorFallback],
  );

  return { run, isPending, errorMessage, setErrorMessage };
}
